import React, { useMemo } from 'react';
import { Point } from '@/types/common';
import { ScaleData } from '@/types/floorPlanViewer';
import {
  calculateWallDimensions,
  calculatePolygonArea,
  formatArea,
  calculateDimensionOffset,
  getAreaLabelPosition,
} from '@/utils/scaleCalculations';

interface RoomDimensionsProps {
  points: Point[];
  scale: number;
  scaleData: ScaleData | null;
  showDimensions?: boolean;
  showArea?: boolean;
  isSelected?: boolean;
  opacity?: number;
  offsetDistance?: number;
  roomName?: string;
  showRoomName?: boolean;
  minimalMode?: boolean; // When true, use subtle styling for dimensions-only display
}

export function RoomDimensions({
  points,
  scale,
  scaleData,
  showDimensions = true,
  showArea = true,
  isSelected = false,
  opacity = 1,
  offsetDistance = 20,
  roomName,
  showRoomName = false,
  minimalMode = false,
}: RoomDimensionsProps) {
  // Calculate wall dimensions (with early return check inside)
  const wallDimensions = useMemo(() => {
    if (!scaleData || points.length < 3 || !showDimensions) return [];
    return calculateWallDimensions(points, scaleData);
  }, [points, scaleData, showDimensions]);

  // Calculate area (with early return check inside)
  const area = useMemo(() => {
    if (!scaleData || points.length < 3 || !showArea) return null;
    const pixelArea = calculatePolygonArea(points);
    return formatArea(pixelArea, scaleData);
  }, [points, scaleData, showArea]);

  // Get area label position (with early return check inside)
  const areaPosition = useMemo(() => {
    if (!scaleData || points.length < 3 || !showArea) return null;
    return getAreaLabelPosition(points);
  }, [points, scaleData, showArea]);

  // Early return after all hooks
  if (!scaleData || points.length < 3) return null;

  // Calculate adaptive font size based on zoom
  const fontSize = minimalMode
    ? Math.max(9, Math.min(12, 10 / scale))
    : Math.max(10, Math.min(14, 12 / scale));
  const strokeWidth = minimalMode ? Math.max(0.3, 0.5 / scale) : Math.max(0.5, 1 / scale);
  const arrowSize = Math.max(4, 6 / scale);
  const offset = offsetDistance / scale;

  // Color scheme - more subtle in minimal mode
  const dimensionColor = minimalMode ? '#9ca3af' : isSelected ? '#2563eb' : '#6b7280';
  const areaColor = minimalMode ? '#6b7280' : isSelected ? '#1e40af' : '#374151';
  const bgColor = minimalMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.95)';

  return (
    <g className="room-dimensions" opacity={opacity} pointerEvents="none">
      {/* Wall dimensions */}
      {showDimensions &&
        wallDimensions.map((dim, index) => {
          // Skip very short walls (less than 10 pixels)
          if (dim.distance < 10) return null;

          // Calculate offset position for dimension line
          const offsetLine = calculateDimensionOffset(dim.start, dim.end, offset);

          return (
            <g key={`wall-${index}`} className="wall-dimension">
              {/* Extension lines */}
              <line
                x1={dim.start.x}
                y1={dim.start.y}
                x2={offsetLine.start.x}
                y2={offsetLine.start.y}
                stroke={dimensionColor}
                strokeWidth={strokeWidth}
                strokeOpacity={0.4}
              />
              <line
                x1={dim.end.x}
                y1={dim.end.y}
                x2={offsetLine.end.x}
                y2={offsetLine.end.y}
                stroke={dimensionColor}
                strokeWidth={strokeWidth}
                strokeOpacity={0.4}
              />

              {/* Dimension line with arrows */}
              <line
                x1={offsetLine.start.x}
                y1={offsetLine.start.y}
                x2={offsetLine.end.x}
                y2={offsetLine.end.y}
                stroke={dimensionColor}
                strokeWidth={strokeWidth}
                markerStart="url(#dimension-arrow-start)"
                markerEnd="url(#dimension-arrow-end)"
              />

              {/* Dimension text with background */}
              <g
                transform={`translate(${dim.midpoint.x + offset * Math.cos(dim.angle + Math.PI / 2)}, ${dim.midpoint.y + offset * Math.sin(dim.angle + Math.PI / 2)})`}
              >
                {/* Background rect for readability */}
                <rect
                  x={-dim.formatted.length * fontSize * 0.3}
                  y={-fontSize * 0.7}
                  width={dim.formatted.length * fontSize * 0.6}
                  height={fontSize * 1.4}
                  fill={bgColor}
                  rx={2 / scale}
                  ry={2 / scale}
                />

                {/* Dimension text */}
                <text
                  x={0}
                  y={0}
                  fill={dimensionColor}
                  fontSize={fontSize}
                  fontWeight="500"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(${dim.textAngle})`}
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                >
                  {dim.formatted}
                </text>
              </g>
            </g>
          );
        })}

      {/* Room name label (if shown and no room polygon visible) */}
      {showRoomName && roomName && areaPosition && (
        <g
          className="room-name"
          transform={`translate(${areaPosition.x}, ${areaPosition.y - 10 / scale})`}
        >
          <text
            x={0}
            y={0}
            fill={minimalMode ? '#4b5563' : '#1f2937'}
            fontSize={fontSize * 1.2}
            fontWeight="600"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
              filter: minimalMode ? 'none' : 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.8))',
            }}
          >
            {roomName}
          </text>
        </g>
      )}

      {/* Area label - positioned at center or offset if room name is shown */}
      {showArea && area && areaPosition && (
        <g
          className="room-area"
          transform={`translate(${areaPosition.x}, ${areaPosition.y + (showRoomName && roomName ? 10 / scale : 0)})`}
        >
          {/* Background pill */}
          <rect
            x={-area.length * fontSize * 0.35}
            y={-fontSize * 0.8}
            width={area.length * fontSize * 0.7}
            height={fontSize * 1.6}
            fill={bgColor}
            stroke={minimalMode ? 'transparent' : areaColor}
            strokeWidth={strokeWidth}
            rx={fontSize * 0.8}
            ry={fontSize * 0.8}
          />

          {/* Area text */}
          <text
            x={0}
            y={0}
            fill={areaColor}
            fontSize={fontSize}
            fontWeight={minimalMode ? '500' : '600'}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          >
            {area}
          </text>
        </g>
      )}

      {/* Define arrow markers */}
      <defs>
        <marker
          id="dimension-arrow-start"
          markerWidth={arrowSize}
          markerHeight={arrowSize}
          refX={0}
          refY={arrowSize / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={`M ${arrowSize} 0 L ${arrowSize} ${arrowSize} L 0 ${arrowSize / 2} Z`}
            fill={dimensionColor}
          />
        </marker>
        <marker
          id="dimension-arrow-end"
          markerWidth={arrowSize}
          markerHeight={arrowSize}
          refX={arrowSize}
          refY={arrowSize / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={`M 0 0 L 0 ${arrowSize} L ${arrowSize} ${arrowSize / 2} Z`}
            fill={dimensionColor}
          />
        </marker>
      </defs>
    </g>
  );
}
